/**
 * Staff Portal v2 — feature flag hook (user-isolated).
 *
 * Wraps `/api/production/feature-flags/mine`. Cached in-memory PER USER
 * IDENTITY (`user.id | user.org_id`) so a logout + different login in the
 * same tab does NOT inherit the previous user's flags. Cache is also
 * cleared explicitly by the auth logout flow via clearStaffV2FlagCache().
 *
 * Fail-closed: no identity, request error, or stale in-flight response →
 * empty flag map → legacy everywhere.
 */
import { useEffect, useState, useCallback } from 'react';
import api from './api';
import { useAuth } from './auth';

const V2_FLAGS = [
  'staff_portal_v2_enabled',
  'staff_navigation_v2_enabled',
  'staff_dashboard_v2_enabled',
  'staff_my_work_v2_enabled',
];

let _cache = null;
let _cacheKey = null;
let _inFlight = null;
let _inFlightKey = null;

function _identityKey(user) {
  if (!user) return null;
  const uid = user.id || user.user_id || user._id;
  if (!uid) return null;
  return `${uid}|${user.org_id || ''}`;
}

/** Reset all flag-cache state. Called from logout / user-switch. */
export function clearStaffV2FlagCache() {
  _cache = null;
  _cacheKey = null;
  _inFlight = null;
  _inFlightKey = null;
}

async function _load(identityKey) {
  if (_cache && _cacheKey === identityKey) return _cache;
  if (_inFlight && _inFlightKey === identityKey) return _inFlight;
  _inFlightKey = identityKey;
  _inFlight = (async () => {
    try {
      const res = await api.get('/production/feature-flags/mine');
      // Guard against a stale in-flight response landing after a user switch.
      if (_inFlightKey !== identityKey) return _cache || {};
      const data = res.data || {};
      const map = data.flags || data.data || data;
      _cache = map && typeof map === 'object' ? map : {};
      _cacheKey = identityKey;
      return _cache;
    } catch {
      if (_inFlightKey === identityKey) {
        _cache = {};
        _cacheKey = identityKey;
      }
      return {};
    } finally {
      if (_inFlightKey === identityKey) {
        _inFlight = null;
        _inFlightKey = null;
      }
    }
  })();
  return _inFlight;
}

export function useStaffV2Flags() {
  const { user } = useAuth();
  const identity = _identityKey(user);
  const [flags, setFlags] = useState(() => (_cacheKey === identity ? _cache : null));
  const [loading, setLoading] = useState(() => !(identity && _cacheKey === identity && _cache));

  const refresh = useCallback(async () => {
    if (!identity) {
      clearStaffV2FlagCache();
      setFlags({});
      setLoading(false);
      return;
    }
    _cache = null;
    _cacheKey = null;
    setLoading(true);
    const map = await _load(identity);
    setFlags(map);
    setLoading(false);
  }, [identity]);

  useEffect(() => {
    let cancelled = false;
    if (!identity) {
      clearStaffV2FlagCache();
      setFlags({});
      setLoading(false);
      return () => { cancelled = true; };
    }
    if (_cacheKey && _cacheKey !== identity) {
      clearStaffV2FlagCache();
    }
    _load(identity).then((map) => {
      if (!cancelled) {
        setFlags(map);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [identity]);

  const map = flags || {};
  const portal = !!map.staff_portal_v2_enabled;
  return {
    loading,
    refresh,
    staff_portal_v2_enabled: portal,
    staff_navigation_v2_enabled: portal && !!map.staff_navigation_v2_enabled,
    staff_dashboard_v2_enabled: portal && !!map.staff_dashboard_v2_enabled,
    staff_my_work_v2_enabled: portal && !!map.staff_my_work_v2_enabled,
    // Phase 4A module flags — backend fails closed regardless; these gate UI affordances.
    participant_tasks_enabled: !!map.participant_tasks_enabled,
    staff_learning_enabled: !!map.staff_learning_enabled,
    staff_resources_enabled: !!map.staff_resources_enabled,
    // Phase 4B-1 flags
    staff_employment_self_service_enabled: !!map.staff_employment_self_service_enabled,
    staff_settings_enabled: !!map.staff_settings_enabled,
    raw: map,
  };
}

export { V2_FLAGS };
